import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { CHANNELMENUS_MP_KEY } from 'src/constants/federationStorageKeys';
import { removeIndexDbData, setIndexDbData } from 'src/utils/indexDbStorage';

import { useGetRBData } from './useGetRBData';
import { useGetRBDataHistories } from './useGetRBDataHistories';

export const useRBData = (
  locationCode: string,
  date: string,
  time: string,
  timezone = 'utc',
) => {
  const {
    data: rbDataHistories = [],
    isLoading: isRBDataHistoriesLoading,
    isSuccess: isRBDataHistoriesSuccess,
  } = useGetRBDataHistories(locationCode, date, !!locationCode && !!date);

  const rbVersionByDateAndTime = rbDataHistories.find(
    (version) =>
      dayjs(version.timestamp).valueOf() <=
      dayjs(`${date} ${time}`).tz(timezone, true).valueOf(),
  );

  const {
    data: rbData,
    isLoading: isRBDataLoading,
    isFetching: isRBDataFetching,
    isSuccess: isRBDataSuccess,
  } = useGetRBData(
    locationCode,
    rbVersionByDateAndTime?.version_id ?? '',
    !isRBDataHistoriesLoading &&
      !!locationCode &&
      !!rbVersionByDateAndTime?.version_id,
  );

  const isRBDataCleaned = useRef(false);
  const [isRBDataSynced, setIsRBDataSynced] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      // one-time clean
      if (!isRBDataCleaned.current) {
        localStorage.removeItem(CHANNELMENUS_MP_KEY);
        await removeIndexDbData(CHANNELMENUS_MP_KEY);
        isRBDataCleaned.current = true;
      }
      if (cancelled) return;

      if (!rbData) {
        await removeIndexDbData(CHANNELMENUS_MP_KEY);
        localStorage.removeItem(CHANNELMENUS_MP_KEY);
        if (!cancelled) setIsRBDataSynced(false);

        return;
      }

      await setIndexDbData(CHANNELMENUS_MP_KEY, rbData);
      const jsonData = JSON.stringify(rbData);
      if (new Blob([jsonData]).size / (1024 * 1024) <= 4.5) {
        localStorage.setItem(CHANNELMENUS_MP_KEY, jsonData);
      }
      if (!cancelled) setIsRBDataSynced(true);
    }

    run().catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [rbData]);

  return {
    rbData,
    isRBDataLoading,
    isRBDataFetching,
    isRBDataSuccess,
    isRBDataSynced,
    rbDataHistories,
    isRBDataHistoriesSuccess,
  };
};
